import type { RbacPermissions } from '@common/types/rbac-permissions.type';

import { Injectable, inject } from '@angular/core';
import { type Observable, take } from 'rxjs';
import { map } from 'rxjs/operators';

import type { RbacActions, RbacPermissionsKeys, RbacPermissionsEntity } from './rbac-permissions.model';
import { RbacPermissionsFacade } from './rbac-permissions.facade';

@Injectable({
    providedIn: 'root',
})
export class RbacPermissionsService {
    private readonly rbacPermissionsFacade = inject(RbacPermissionsFacade);

    public proceedUserPermissions(permissions: RbacPermissions): RbacPermissionsEntity {
        const userPermissions = {};

        Object.keys(permissions).forEach((key) => {
            const value = permissions[key];
            if (typeof value === 'string') {
                if (value.includes('x')) {
                    userPermissions[key] = {
                        view: true,
                        edit: true,
                        export: true,
                    };
                    return;
                }

                if (value.includes('w')) {
                    userPermissions[key] = {
                        view: true,
                        edit: true,
                        export: false,
                    };
                    return;
                }

                if (value.includes('r')) {
                    userPermissions[key] = {
                        view: true,
                        edit: false,
                        export: false,
                    };
                    return;
                }

                userPermissions[key] = {
                    view: false,
                    edit: false,
                    export: false,
                };
            }

            if (typeof value === 'boolean') {
                if (value) {
                    userPermissions[key] = {
                        view: true,
                        edit: true,
                        export: true,
                    };
                } else {
                    userPermissions[key] = {
                        view: false,
                        edit: false,
                        export: false,
                    };
                }
            }

            if (Array.isArray(value)) {
                userPermissions[key] = {};
                value.forEach((item) => {
                    userPermissions[key][item] = true;
                });
            }
        });

        return userPermissions as RbacPermissionsEntity;
    }

    public hasUserPermission$(permission: RbacPermissionsKeys, action: RbacActions): Observable<boolean> {
        return this.rbacPermissionsFacade.permissions$.pipe(
            take(1),
            map((permissions) => {
                if (!permissions[permission]) {
                    return false;
                }

                return Boolean(permissions[permission][action]);
            })
        );
    }
}
